function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/* ===== Custom Product Builder Logic Start ===== */

{
  // Scope the variables and functions to this block
  const customBuilderSelector = '.custom-product-builder-container';
  const ALT_TEXT_SEPARATOR = '|';
  const COORDINATE_SEPARATOR = ',';
  const FABRIC_VERSION = '5.3.0'; // Specify Fabric.js version (Reverted to 5.3.0 from 6.0.0)
  const UPLOADCARE_VERSION = '3.x'; // Specify Uploadcare version range
  const UPLOADCARE_PUBLIC_KEY = 'ee46dedb8840d60caecf'; // Use the provided key

  // Define standard printable area dimensions in inches
  const PRINT_AREA_DIMENSIONS = {
    'Front': { width: 13, height: 17 },
    'Back': { width: 13, height: 17 },
    'Left Sleeve': { width: 5.5, height: 5.5 }, // Updated
    'Right Sleeve': { width: 5.5, height: 5.5 }, // Updated
    'Left Long Sleeve': { width: 4, height: 13 },
    'Right Long Sleeve': { width: 4, height: 13 },
    'Front Hoodie': { width: 13, height: 8 },
    'Hat Front': { width: 5, height: 3 },
    'Hat Side': { width: 2, height: 2 },
    'Hat Back': { width: 2, height: 1 },
    // Add other views as needed, ensure keys match alt text Print View exactly
    // Default can be handled in the function if needed
  };

  // Comprehensive color map for color swatches
  const COLOR_MAP = {
    'acid-black': '#4A4A4A',
    'aloe': '#f86a68',
    'alpine-green': '#2a303c',
    'amethyst': '#232426',
    'antique-cherry-red': '#EB7783',
    'antique-heliconia': '#b7417e',
    'antique-irish-green': '#049557',
    'antique-jade-dome': '#03898D',
    'antique-orange': '#cb4d2c',
    'antique-royal': '#1b4889',
    'antique-sapphire': '#047a9f',
    'aqua': '#535253',
    'army': '#5c5741',
    'army-heather': '#454430',
    'ash': '#e1e0e8',
    'ash-grey': '#b3b3b3',
    'ash-heather': '#B2BEB5',
    'asphalt': '#595959',
    'athletic-heather': '#9d9ca4',
    'autumn': '#9f4a29',
    'azalea': '#f680b1',
    'baby-blue': '#a9c0d7',
    'bay': '#262520',
    'berry': '#af1861',
    'black': '#1b1b1f',
    'black-camo': '#48474c',
    'black-heather': '#444348',
    'blackberry': '#382f47',
    'blossom': '#e6b4be',
    'blue-aqua': '#acd9ee',
    'blue-dusk': '#343945',
    'blue-jean': '#637185',
    'blue-spruce': '#5e746d',
    'bone': '#e6dfcf',
    'brick': '#a15555',
    'bright-salmon': '#f86868',
    'brown': '#58332b',
    'brown-savana': '#a58a7d',
    'burgundy': '#692638',
    'burnt-orange': '#e17f49',
    'butter': '#fae0a6',
    'cactus': '#7e7b5c',
    'cantaloupe': '#feb684',
    'canvas-red': '#941c26',
    'cardinal': '#a80936',
    'cardinal-red': '#8d1a2f',
    'caribbean-blue': '#2688ad',
    'caribbean-mist': '#02bde8',
    'carolina-blue': '#8eb2e5',
    'carolina-blue-mist': '#5486c5',
    'celadon': '#c8efec',
    'cement': '#bab6b3',
    'chalky-mint': '#9cd9d5',
    'chambray': '#c7dbe6',
    'charcoal': '#615e5c',
    'charcoal-grey': '#49484d',
    'charcoal-heather': '#343031',
    'charity-pink': '#e7818d',
    'cherry-red': '#b01b3b',
    'chocolate': '#3d3331',
    'citrus': '#faba56',
    'classic-navy': '#303247',
    'classic-navy-heather': '#40424f',
    'classic-pink': '#e8ced7',
    'clay': '#B66A50',
    'cobalt': '#313984',
    'cocoa': '#D2691E',
    'columbia-blue': '#2b619b',
    'cool-mint': '#8ad5b6',
    'coral': '#FE6549',
    'coral-silk': '#D6576A',
    'cornsilk': '#f4e28d',
    'cranberry': '#9c1e36',
    'cream': '#fff0d9',
    'creme': '#efdfd0',
    'crimson': '#b85761',
    'crunchberry': '#c35474',
    'cyber-pink': '#c4437c',
    'daisy': '#fbc853',
    'dark-chocolate': '#4c3b3a',
    'dark-green': '#384f3d',
    'dark-grey': '#353135',
    'dark-grey-heather': '#343031',
    'dark-heather': '#4d555a',
    'dark-lavender': '#a19cbf',
    'dark-maroon': '#58383d',
    'dark-navy': '#383b4c',
    'deep-heather': '#696e68',
    'deep-navy': '#263650',
    'deep-purple': '#473066',
    'deep-red': '#eb0623',
    'deep-royal': '#1f4286',
    'deep-teal': '#274450',
    'denim': '#515763',
    'dtg-black': '#0f0f0f',
    'dtg-dark-grey': '#3d3c41',
    'dtg-white': '#faf9fe',
    'dune-mist': '#a28f6f',
    'dusty-blue': '#a5baae',
    'dusty-green': '#A9BA9D',
    'dusty-rose': '#eac8bc',
    'eggplant': '#563351',
    'electric-green': '#6cc161',
    'evergreen': '#224a33',
    'fine-black': '#2b2724',
    'fine-charcoal': '#5d5a55',
    'fine-kelly-green': '#01855d',
    'fine-navy': '#303247',
    'fine-red': '#db1024',
    'fine-turquoise': '#0095c0',
    'fine-white': '#f3f2f7',
    'flo-blue': '#6b86d0',
    'forest': '#24352b',
    'forest-camo': '#585a4c',
    'forest-green': '#30372d',
    'fuchsia': '#d2305d',
    'galapagos-blue': '#165d68',
    'garnet': '#87132d',
    'gold': '#fbad1b',
    'granite': '#9a9ca0',
    'graphite': '#34373e',
    'graphite-heather': '#88868b',
    'grass': '#6ebd7b',
    'gravel': '#aeaaab',
    'grey': '#978e8d',
    'grey-heather': '#cdccd1',
    'gunmetal': '#8c9093',
    'gunmetal-heather': '#8b8a8f',
    'heather-aqua': '#57b2e1',
    'heather-autumn': '#b75333',
    'heather-berry': '#d56798',
    'heather-blue': '#5967a4',
    'heather-blue-lagoon': '#88a4a5',
    'heather-brown': '#533d30',
    'heather-bubble-gum': '#ffc9e6',
    'heather-burgundy': '#7e4951',
    'heather-canvas-red': '#95282d',
    'heather-cardinal': '#9a4b5a',
    'heather-cardinal-red': '#cb6875',
    'heather-caribbean-blue': '#3ad6ec',
    'heather-cement': '#e9e1de',
    'heather-charcoal': '#5f5f5f',
    'heather-charity-pink': '#fb89ab',
    'heather-clay': '#bf655a',
    'heather-columbia-blue': '#617cc3',
    'heather-cool-grey': '#d8cecd',
    'heather-dark-green': '#758b7e',
    'heather-dark-grey': '#636363',
    'heather-dark-maroon': '#8b666e',
    'heather-dark-navy': '#7f8196',
    'heather-deep-royal': '#516cb1',
    'heather-deep-teal': '#62848e',
    'heather-dust': '#d4c7be',
    'heather-dusty-blue': '#7c968d',
    'heather-emerald': '#212b2c',
    'heather-forest': '#415242',
    'heather-fuchsia': '#ec6988',
    'heather-galapagos-blue': '#3f9db1',
    'heather-graphite': '#3d4047',
    'heather-grass-green': '#2f6f54',
    'heather-green': '#808d55',
    'heather-grey': '#bfbec3',
    'heather-heliconia': '#f76c9d',
    'heather-ice-blue': '#dfe8ed',
    'heather-indigo': '#7e86a8',
    'heather-irish-green': '#089a69',
    'heather-kelly': '#06a972',
    'heather-lapis': '#8a72ac',
    'heather-magenta': '#ad5d82',
    'heather-marmalade': '#f29d32',
    'heather-maroon': '#79535d',
    'heather-mauve': '#c07f7d',
    'heather-midnight-navy': '#383b42',
    'heather-military-green': '#646a5e',
    'heather-mint': '#bde3cc',
    'heather-mustard': '#da8f2a',
    'heather-natural': '#f7efe4',
    'heather-navy': '#63667c',
    'heather-olive': '#7b6a50',
    'heather-orange': '#f1795c',
    'heather-orchid': '#cf9b9f',
    'heather-peach': '#efc0a6',
    'heather-prism-blue': '#c6d1e3',
    'heather-prism-dusty-blue': '#a5bbae',
    'heather-prism-ice-blue': '#cfe7e7',
    'heather-prism-lilac': '#e9bfe5',
    'heather-prism-mint': '#b2ccbf',
    'heather-prism-natural': '#e0daca',
    'heather-prism-peach': '#d2aea2',
    'heather-prism-sunset': '#e19681',
    'heather-purple': '#6c5d8c',
    'heather-radiant-orchid': '#d88ac6',
    'heather-raspberry': '#9b3d4f',
    'heather-red': '#d53d55',
    'heather-royal': '#6b90cc',
    'heather-sage': '#96a294',
    'heather-sand-dune': '#edceaf',
    'heather-sapphire': '#3ca0ca',
    'heather-scarlet-red': '#c35860',
    'heather-sea-green': '#3fd9bd',
    'heather-slate': '#677784',
    'heather-stone': '#aba19f',
    'heather-storm': '#858086',
    'heather-sunset': '#e49881',
    'heather-tan': '#c3af96',
    'heather-team-purple': '#644a65',
    'heather-true-royal': '#427cc6',
    'heather-yellow': '#ffdf5a',
    'heather-yellow-gold': '#fdc459',
    'heliconia': '#f12c79',
    'hemp': '#5b634b',
    'ice-blue': '#7e9ba8',
    'ice-grey': '#d9d1d0',
    'icy-rock': '#A9C0C6',
    'indigo-blue': '#5a758a',
    'iris': '#568bc4',
    'irish-green': '#02B163',
    'iron': '#534a4d',
    'island-reef': '#9ddabd',
    'ivory': '#e9e1d5',
    'j.-navy': '#26283d',
    'jade-dome': '#049ca4',
    'jet-black-heather': '#3e3d42',
    'kelly': '#0a4c2f',
    'kelly-green': '#edf9f8',
    'kelly-green-heather': '#0e9672',
    'kiwi': '#a8c076',
    'lagoon': '#6bc4d2',
    'lavender': '#d2c1f5',
    'leaf': '#447542',
    'light-blue': '#c6d7e7',
    'light-blue-heather': '#8aa9d7',
    'light-green': '#597f70',
    'light-pink': '#f0cad6',
    'light-yellow': '#ffeab8',
    'lilac': '#5c498d',
    'lime': '#b1e17a',
    'maize-yellow': '#efcf67',
    'maroon': '#6c1a38',
    'maroon-mist': '#743546',
    'mauve': '#c8857b',
    'metro-blue': '#4e5886',
    'midnight': '#1f4655',
    'military-green': '#5d654d',
    'mint': '#b6dcbe',
    'mint-green': '#c5e6c2',
    'monterey-sage': '#616858',
    'moss': '#7c8373',
    'mustard': '#e19d28',
    'mustard-heather': '#dc9d34',
    'natural': '#ebe1da',
    'navy': '#293347',
    'navy-blue': '#000080',
    'navy-mist': '#3d4359',
    'neo-mint': '#d3edc8',
    'neon-blue': '#045a9e',
    'neon-green': '#aef27d',
    'neon-pink': '#fa78ae',
    'new-silver': '#dbd7d4',
    'oatmeal-heather': '#cbcbcb',
    'ocean-blue': '#6facc9',
    'off-black': '#4A4A4A',
    'off-white': '#f9f4e1',
    'old-gold': '#d9a36f',
    'olive': '#514e1e',
    'orange': '#fa5522',
    'orchid': '#ce9a95',
    'oxblood-black': '#28181a',
    'oxford': '#8e8e8e',
    'paragon': '#C29CA8',
    'peach': '#fdcd85',
    'pebble-brown': '#705d5d',
    'pepper': '#525153',
    'periwinkle': '#7f89c6',
    'pfd-white': '#f0ebef',
    'pink': '#f5bdb6',
    'pistachio': '#D7E2BC',
    'pitch-black': '#27262b',
    'pitch-black-mist': '#464147',
    'platinum': '#c6c7c2',
    'plumrose': '#eca4b2',
    'poppy': '#e8201e',
    'powder-blue': '#ccdcec',
    'prairie-dust': '#817B61',
    'purist-blue': '#a3d5dc',
    'purple': '#422c72',
    'red': '#d42d41',
    'red-mist': '#ce3740',
    'retro-heather-coral': '#fd7751',
    'rock': '#978e87',
    'royal': '#145eac',
    'royal-blue': '#0384ec',
    'royal-caribe': '#3d81b0',
    'royal-heather': '#215b8d',
    'royal-mist': '#4194d6',
    'russet': '#634344',
    'rust': '#7b3021',
    'saddle': '#956746',
    'safety-green': '#d7f249',
    'safety-orange': '#FE7301',
    'safety-pink': '#fb789f',
    'safety-yellow': '#cedf5b',
    'sage': '#A3AD98',
    'sand': '#d7c6ba',
    'sandstone': '#928c7c',
    'sapphire': '#008CB9',
    'scarlet-red': '#c01d38',
    'scuba-blue': '#5fd3d2',
    'seafoam': '#76aaa3',
    'silver': '#d4d1ca',
    'sky': '#73bede',
    'sky-blue': '#A4C1DF',
    'slate': '#d5cdc2',
    'slate-blue': '#3d3f54',
    'smoke': '#44403d',
    'snow-camo': '#3f3f3f',
    'soft-cream': '#edd0ac',
    'soft-pink': '#fbe3e1',
    'solid-black-blend': '#1b1a1f',
    'solid-white-blend': '#ebeaef',
    'sport-grey': '#bbbcca',
    'sports-grey': '#bfc1cd',
    'steel-blue': '#656a73',
    'stone-blue': '#96a7b9',
    'storm': '#98898d',
    'storm-grey': '#bcb8b5',
    'sunset': '#ea7c56',
    'sunset-coral': '#f07965',
    'tan': '#d4bca5',
    'tangerine': '#EFA586',
    'teal': '#33ab95',
    'teal-ice': '#c6e0d3',
    'team-navy': '#24295a',
    'team-purple': '#401d64',
    'tennessee-orange': '#FF9D03',
    'terracotta': '#f69c8c',
    'texas-orange': '#c6552d',
    'tropical-blue': '#04a2b2',
    'true-navy': '#242e41',
    'true-red': '#af1632',
    'true-royal': '#254daa',
    'truffle': '#522730',
    'turf-green': '#0d8a4d',
    'turquoise': '#52c0dd',
    'tweed': '#524f50',
    'vegas-gold': '#664837',
    'vineyard': '#744952',
    'vintage-black': '#202526',
    'vintage-heather-blue': '#5e739e',
    'vintage-heather-navy': '#444561',
    'vintage-heather-red': '#ba5565',
    'vintage-white': '#e7e0dd',
    'violet': '#868cbe',
    'vivid-lime': '#9ee587',
    'washed-denim': '#8397b2',
    'watermelon': '#f26f77',
    'white': '#edebf1',
    'yam': '#b87251',
    'yellow': '#f3c763',
    'yellow-haze': '#fad7a1'
  };

  /**
   * Loads an external script dynamically.
   * @param {string} url - The URL of the script to load.
   * @param {string} id - An ID to assign to the script tag.
   * @returns {Promise<void>} A promise that resolves when the script is loaded.
   */
  function loadScript(url, id) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) {
        resolve(); // Already loaded
        return;
      }
      const script = document.createElement('script');
      script.src = url;
      script.id = id;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function initializeBuilders() {
    const containers = document.querySelectorAll(customBuilderSelector);
    if (containers.length === 0) {
      // No builder sections found on this page
      return;
    }
    console.log(`Found ${containers.length} custom builder containers. Initializing...`);

    containers.forEach(container => {
      // Initialize data storage for this builder instance
      container._customBuilderData = {
        fabricCanvas: null,
        mergedData: null,
        imagesByColorAndView: null,
        printableAreaRect: null,
        uploadedArtworkByView: {}, // Store artwork per print view
        uploadedFileInfoByView: {}, // Store Uploadcare fileInfo per view
        uploadcareWidget: null, // Store the widget instance
        currentlyDisplayedArtworkObject: null, // Track object on canvas
        currentScaleFactor: null,
        displayScaleFactor: null // Added in previous step
      };
      initializeBuilderInstance(container);
    });
  }

  async function initializeBuilderInstance(container) {
    const sectionId = container.dataset.sectionId;
    console.log(`Initializing builder for section ${sectionId}`);

    const productJsonElement = document.getElementById(`ProductJson-${sectionId}`);
    const currentProductHandle = container.dataset.currentProductHandle;
    const groupingsJsonUrl = container.dataset.groupingsUrl; // Get the URL from the data attribute
    
    let productData = null;
    let groupedProductHandles = [currentProductHandle]; // Default to current handle
    let groupingsData = null; // Variable to store fetched grouping data

    // Initialize internal state object
    if (!container._customBuilderData) {
        container._customBuilderData = { 
            selectedColor: null,
            selectedPrintView: null,
            artworkInfo: {},
            fabricCanvas: null,
            printableAreaRect: null,
            scaleFactor: null,
            topLeftCoords: null,
            imagesByColorAndView: {},
            mergedData: null
        };
    }

    // Parse Main Product Data
    if (productJsonElement) {
      try {
        productData = JSON.parse(productJsonElement.textContent);
        console.log(`Main Product Data for section ${sectionId}:`, productData);
        const outputElement = document.getElementById(`product-json-output-${sectionId}`);
        if (outputElement) {
          outputElement.textContent = JSON.stringify(productData, null, 2);
        }
      } catch (error) {
        console.error(`Error parsing main product JSON for section ${sectionId}:`, error);
      }
    } else {
      console.error(`Main Product JSON element not found for section ${sectionId}`);
    }

    // Fetch and Parse Grouping Data from assets using the URL
    if (groupingsJsonUrl) {
        try {
          const response = await fetch(groupingsJsonUrl); // Use the URL from the data attribute
          if (response.ok) {
            groupingsData = await response.json();
            console.log("Fetched groupings data:", groupingsData);
            
            // Find the group for the current product
            if (groupingsData && groupingsData.groups && Array.isArray(groupingsData.groups)) {
              const foundGroup = groupingsData.groups.find(group => 
                group.products && Array.isArray(group.products) && group.products.includes(currentProductHandle)
              );

              if (foundGroup) {
                groupedProductHandles = foundGroup.products;
                console.log(`Found product group for ${currentProductHandle}. Handles:`, groupedProductHandles);
              } else {
                console.log(`No group found containing ${currentProductHandle} in fetched JSON. Using default handles.`);
              }
            } else {
              console.warn(`Fetched groupings JSON for section ${sectionId} has incorrect structure. Using default handles.`);
            }
          } else {
            console.error(`Failed to fetch ${groupingsJsonUrl}: ${response.status} ${response.statusText}`);
          }
        } catch (error) {
          console.error(`Error fetching or parsing ${groupingsJsonUrl}:`, error);
        }
    } else {
        console.error('Grouping JSON URL not found in data attribute.');
    }

    // Update the debug output for grouped handles
    console.log(`Grouped Product Handles to fetch for section ${sectionId}:`, groupedProductHandles);
    const groupingOutputElement = document.getElementById(`grouping-json-output-${sectionId}`);
    if (groupingOutputElement) {
      groupingOutputElement.textContent = JSON.stringify(groupedProductHandles, null, 2);
    }

    // Fetch and Merge Grouped Product Data
    if (Array.isArray(groupedProductHandles) && groupedProductHandles.length > 0 && productData) {
      console.log(`Fetching full data for ${groupedProductHandles.length} products...`);
      fetchGroupedProductData(groupedProductHandles)
        .then(groupedProducts => {
          const mergedData = {
            mainProduct: productData, 
            groupedProducts: groupedProducts
          };
          console.log("Merged Product Data:", mergedData);
          // Initialize or update the state object on the container
          container._customBuilderData.mergedData = mergedData; 
          renderUI(container); // Pass container instead of mergedData
        })
        .catch(error => {
          console.error("Error fetching or merging grouped product data:", error);
        });
    } else if (productData) {
      console.log("Product is not part of a group or fetching failed. Rendering with main product data only.");
      // Initialize or update the state object on the container
      container._customBuilderData.mergedData = { mainProduct: productData, groupedProducts: [] };
      renderUI(container); // Pass container instead of mergedData
    }
  }

  async function fetchGroupedProductData(handles) {
    const fetchPromises = handles.map(handle => {
      if (typeof handle !== 'string' || handle.trim() === '') {
        console.warn(`Invalid product handle skipped: ${handle}`);
        return Promise.resolve(null); 
      }
      const url = `/products/${handle.trim()}.js`;
      return fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} for handle: ${handle}`);
          }
          return response.json();
        })
        .catch(error => {
          console.error(`Failed to fetch product data for handle: ${handle}`, error);
          return null; 
        });
    });

    const results = await Promise.all(fetchPromises);
    return results.filter(product => product !== null);
  }

  // Debounce function (adjust wait time as needed)
  function debounce(func, wait, immediate) {
      var timeout;
      return function() {
          var context = this, args = arguments;
          var later = function() {
              timeout = null;
              if (!immediate) func.apply(context, args);
          };
          var callNow = immediate && !timeout;
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
          if (callNow) func.apply(context, args);
      };
  };

  // --- Initialize Everything --- 
  // Store resize handlers globally to manage them
  const resizeHandlers = {}; 

  // Ensure external libraries are loaded before initializing
  if (document.readyState === 'complete' || (document.readyState !== 'loading' && !document.documentElement.doScroll)) {
    // DOMContentLoaded has already fired
    console.log("DOM already loaded. Loading libraries...");
    Promise.all([
      loadScript(`https://cdnjs.cloudflare.com/ajax/libs/fabric.js/${FABRIC_VERSION}/fabric.min.js`, 'fabric-js-script'), 
      loadScript(`https://ucarecdn.com/libs/widget/${UPLOADCARE_VERSION}/uploadcare.full.min.js`, 'uploadcare-js-script')
    ]).then(() => {
      console.log("Libraries loaded.");
      initializeBuilders();
    }).catch(error => {
      console.error("Failed to load external libraries:", error);
    });
  } else {
    // DOMContentLoaded hasn't fired yet
    document.addEventListener('DOMContentLoaded', async () => {
      console.log("DOM Loaded event fired. Loading libraries...");
      try {
        await Promise.all([
          loadScript(`https://cdnjs.cloudflare.com/ajax/libs/fabric.js/${FABRIC_VERSION}/fabric.min.js`, 'fabric-js-script'), 
          loadScript(`https://ucarecdn.com/libs/widget/${UPLOADCARE_VERSION}/uploadcare.full.min.js`, 'uploadcare-js-script')
        ]);
        console.log("External libraries (Fabric.js, Uploadcare) loaded successfully via listener.");
        initializeBuilders();
      } catch (error) {
        console.error("Failed to load external libraries via listener:", error);
      }
    });
  }

  // Helper function to initialize Fabric.js canvas
  function _initializeFabricCanvas(container, canvasElement, previewColumn, width, height) {
    const sectionId = container.dataset.sectionId;
    const builderData = container._customBuilderData;
    
    try {
        const previewRect = previewColumn.getBoundingClientRect();
        if (previewRect.width <= 0 || previewRect.height <= 0) {
            console.warn(`[_initializeFabricCanvas] Attempted init for section ${sectionId}, but dimensions still invalid. Aborting this attempt.`);
            return false; // Indicate failure
        }

        // --- Remove setting element attributes --- 
        // canvasElement.width = width;   // Use passed-in width
        // canvasElement.height = height; // Use passed-in height
        
        // --- Pass dimensions directly to Fabric constructor --- 
        builderData.fabricCanvas = new fabric.Canvas(canvasElement, {
            width: width,   // Pass width directly
            height: height,  // Pass height directly
            preserveObjectStacking: true,
            allowTouchScrolling: false // <<< Set to false to prioritize object interaction
        });
        console.log(`Fabric.js canvas initialized successfully for section ${sectionId} with dimensions ${width}x${height}`); // Log the dimensions WE passed
        
        // <<< Check method immediately after initialization >>>
        console.log(`[_initializeFabricCanvas - ${sectionId}] typeof builderData.fabricCanvas.setBackgroundImage:`, typeof builderData.fabricCanvas.setBackgroundImage);
        console.log(`[_initializeFabricCanvas - ${sectionId}] canvas.allowTouchScrolling value:`, builderData.fabricCanvas.allowTouchScrolling); // <<< ADD THIS LOG

        builderData.fabricCanvas.on({
            'object:moving': (e) => checkBounds(e.target, container),
            'object:scaling': (e) => checkBounds(e.target, container),
            'object:modified': (e) => updateArtworkDimensionsDisplay(e.target, container)
        });

        // --- Setup Resize Listener for the Canvas --- 
        // Remove existing handler if it exists for this section
        if (resizeHandlers[sectionId]) {
            resizeHandlers[sectionId].observer.disconnect();
            console.log(`[Canvas Init - ${sectionId}] Disconnected previous resize observer.`);
        }
        
        // <<< REMOVE ATTACHMENT OF THE ONGOING RESIZE OBSERVER >>>
        /* 
        const debouncedRedraw = debounce(() => {
            const currentRect = previewColumn.getBoundingClientRect();
            if (currentRect.width > 0 && currentRect.height > 0) {
                console.log(`[Debounced Resize - ${sectionId}] Resizing canvas to:`, currentRect.width, currentRect.height);
                builderData.fabricCanvas.setWidth(currentRect.width);
                builderData.fabricCanvas.setHeight(currentRect.height);
                _redrawCanvasBackgroundAndOverlay(container); // Call the redraw function
            } else {
                console.warn(`[Debounced Resize - ${sectionId}] Column dimensions are zero. Skipping redraw.`);
            }
        }, 250); // 250ms debounce time

        const resizeObserver = new ResizeObserver(debouncedRedraw);
        resizeObserver.observe(previewColumn);
        resizeHandlers[sectionId] = { observer: resizeObserver }; // Store the handler
        console.log(`[Canvas Init - ${sectionId}] Attached resize observer to preview column.`);
        */
        // --- End Setup Resize Listener ---
        
        return true; // Indicate success
    } catch (error) {
        console.error(`Error initializing Fabric.js canvas for section ${sectionId}:`, error);
        return false; // Indicate failure
    }
  }

  function renderUI(container) { 
    const sectionId = container.dataset.sectionId;
    console.log(`Rendering UI for section ${sectionId}`);
    const controlsColumn = container.querySelector('.builder-controls-column');
    const previewColumn = container.querySelector('.builder-preview-column');
    const canvasElement = container.querySelector(`#builder-canvas-${sectionId}`);
    const builderData = container._customBuilderData; // Use builderData consistently
    const mergedData = builderData.mergedData;

    if (controlsColumn && previewColumn && canvasElement && mergedData && mergedData.mainProduct) {
        
      // --- RE-ADD flag checks ---
      if (builderData.canvasReady) {
          console.log(`[renderUI - ${sectionId}] Canvas already ready. Calling _finishRenderUI.`);
          _finishRenderUI(container);
          return; // Don't setup observer again
      }
      if (builderData.observerSetupInProgress) {
          console.log(`[renderUI - ${sectionId}] Observer setup already in progress. Skipping.`);
          return;
      }
      // --- END RE-ADD flag checks ---

      // --- Initialize Fabric Canvas START (Revert to check-based logic) ---
      if (!builderData.fabricCanvas) {
          console.log(`[renderUI - ${sectionId}] Canvas not found. Setting up ResizeObserver.`);
          builderData.observerSetupInProgress = true; // Set flag
          // Pass elements to observer setup function
          setupCanvasResizeObserver(container, canvasElement, previewColumn, controlsColumn);
      } else {
           console.log(`[renderUI - ${sectionId}] Canvas found but not marked ready? Should not happen if canvasReady flag is used.`);
           // If canvas exists but wasn't marked ready, try finishing UI anyway
           _finishRenderUI(container);
      }
      // --- Initialize Fabric Canvas END ---
      
    } else {
      console.error("Could not find required elements or merged data for renderUI.");
    }
  }

  // Separate function to set up the observer for initial canvas creation
  function setupCanvasResizeObserver(container, canvasElement, previewColumn, controlsColumn) {
      const sectionId = container.dataset.sectionId;
      const builderData = container._customBuilderData; // Get builderData
      let observer;
      const observerCallback = (entries) => {
          // --- REVERT Observer Logic ---
          const entry = entries[0];
          const { width, height } = entry.contentRect;
          console.log(`[ResizeObserver Callback - ${sectionId}] Dimensions:`, width, height);
          
          if (builderData.canvasReady) { // Use canvasReady flag
              console.log(`[ResizeObserver Callback - ${sectionId}] Canvas already ready. Disconnecting.`);
              observer.disconnect();
              builderData.observerSetupInProgress = false; // Reset flag
              return;
          }

          if (width > 0 && height > 0) {
              console.log(`[ResizeObserver Callback - ${sectionId}] Valid dimensions detected. Initializing canvas...`);
              
              // --- Check for essential elements again INSIDE the callback --- 
              if (!canvasElement || !previewColumn || !controlsColumn) {
                  console.error(`[ResizeObserver Callback - ${sectionId}] Essential DOM elements missing. Cannot initialize.`);
                  if (controlsColumn) { 
                     controlsColumn.innerHTML = '<p>Error: Could not initialize preview area (elements missing).</p>';
                  } else {
                     console.error(`[ResizeObserver Callback - ${sectionId}] Controls column also missing, cannot display error.`);
                  }
                  builderData.observerSetupInProgress = false; // Reset flag
                  observer.disconnect(); // Stop trying
                  return;
              }
              // --- End element check ---
              
              const success = _initializeFabricCanvas(container, canvasElement, previewColumn, width, height);
              if (success) {
                 console.log(`[ResizeObserver Callback - ${sectionId}] Canvas initialized successfully via observer.`);
                 builderData.canvasReady = true; // Set the ready flag
                 // --- Call _finishRenderUI --- 
                 console.log(`[ResizeObserver Callback - ${sectionId}] Calling _finishRenderUI.`);
                 _finishRenderUI(container);
                 // --- Disconnect observer AFTER finishing UI --- 
                 console.log(`[ResizeObserver Callback - ${sectionId}] Disconnecting initial setup observer.`);
                 observer.disconnect(); 
              } else {
                  console.error(`[ResizeObserver Callback - ${sectionId}] Canvas initialization failed.`);
                  // Disconnect anyway
                  observer.disconnect();
              }
              builderData.observerSetupInProgress = false; // Reset flag regardless of success/failure after attempt
          }
          // --- END REVERT Observer Logic ---
      };
      
      observer = new ResizeObserver(observerCallback);
      observer.observe(previewColumn);
      console.log(`[setupCanvasResizeObserver] ResizeObserver attached to previewColumn for section ${sectionId}.`);
  }

  function renderColorSwatches(uniqueColors, parentElement, container) {
    const sectionId = container.dataset.sectionId;
    const swatchContainer = document.createElement('div');
    swatchContainer.className = 'dynamic-control swatch-container';
    swatchContainer.innerHTML = '<h4>Color</h4>';
    const colorGrid = document.createElement('div');
    colorGrid.className = 'swatch-grid';

    uniqueColors.forEach((color, index) => {
        const swatch = document.createElement('div'); // The actual color swatch
        swatch.className = 'color-swatch';
        swatch.dataset.color = color;
        swatch.title = color; // Tooltip with color name

        // Get hex color, handle potential case issues and missing colors
        const colorKey = color.toLowerCase().replace(/\s+/g, '-'); // Convert 'Vintage White' to 'vintage-white'
        console.log(`[Swatch Render] Color: "${color}", Generated Key: "${colorKey}"`); // DEBUG
        const hexColor = COLOR_MAP[colorKey] || '#ffffff'; // Default to white if not found

        swatch.style.backgroundColor = hexColor;

        if (index === 0) {
            swatch.classList.add('active'); // Simple active class on the swatch itself
        }

        swatch.addEventListener('click', handleSelectionChange);
        colorGrid.appendChild(swatch); // Add swatch directly to grid
    });
    swatchContainer.appendChild(colorGrid); // Add grid to main container
    parentElement.appendChild(swatchContainer);
  }

  function renderPrintViews(uniquePrintViews, parentElement, container) {
      const sectionId = container.dataset.sectionId;
      const builderData = container._customBuilderData; // Access builder data
      const printViewContainer = document.createElement('div');
      printViewContainer.className = 'dynamic-control print-view-container';
      // Update title and add span for dynamic selected view name
      printViewContainer.innerHTML = `<h4>Choose your Print Areas: <span id="selected-print-area-title-${sectionId}"></span></h4>`; 
      
      const printViewBoxesWrapper = document.createElement('div'); // Wrapper for flex layout
      printViewBoxesWrapper.className = 'print-view-boxes-wrapper';

      uniquePrintViews.forEach((view, index) => {
          // --- Find an image source for this view ---
          let imageSrc = null;
          const selectedColor = builderData.selectedColor;
          // Try selected color first
          if (selectedColor && builderData.imagesByColorAndView[selectedColor] && builderData.imagesByColorAndView[selectedColor][view]) {
              imageSrc = builderData.imagesByColorAndView[selectedColor][view].src;
          } else {
              // Fallback: find the first color that has this view
              const firstColorWithView = Object.keys(builderData.imagesByColorAndView).find(
                  color => builderData.imagesByColorAndView[color][view]
              );
              if (firstColorWithView) {
                  imageSrc = builderData.imagesByColorAndView[firstColorWithView][view].src;
              }
          }
          // --- End Find Image Source ---

          const box = document.createElement('div'); // Changed from button to div
          box.className = 'print-view-box'; // New class for styling
          box.dataset.printView = view;

          if (imageSrc) {
              const img = document.createElement('img');
              img.className = 'print-view-image';
              img.src = imageSrc;
              img.alt = `${view} view`;
              box.appendChild(img);
          } else {
              // Optional: Placeholder if no image found
              const placeholder = document.createElement('div');
              placeholder.className = 'print-view-image-placeholder';
              placeholder.textContent = 'No Preview';
              box.appendChild(placeholder);
          }

          const label = document.createElement('div');
          label.className = 'print-view-label';
          label.textContent = view;
          box.appendChild(label);

          if (index === 0) {
              box.classList.add('active');
              // Update the initial selected view title span
              const titleSpan = printViewContainer.querySelector(`#selected-print-area-title-${sectionId}`);
              if (titleSpan) titleSpan.textContent = view;
          }

          box.addEventListener('click', handleSelectionChange); // Attach listener to the box
          printViewBoxesWrapper.appendChild(box); // Add box to the wrapper
      });
      printViewContainer.appendChild(printViewBoxesWrapper); // Add wrapper to the main container
      parentElement.appendChild(printViewContainer);
  }
  
  function renderUploadArea(parentElement, container) {
      const sectionId = container.dataset.sectionId;
      const uploadArea = document.createElement('div');
      uploadArea.className = 'upload-area dynamic-control';
      uploadArea.innerHTML = `
          <h4>Artwork</h4>
          <input 
            type="hidden" 
            role="uploadcare-uploader" 
            data-public-key="${UPLOADCARE_PUBLIC_KEY}" 
            data-tabs="file url facebook gdrive instagram"
            data-multiple="false"
            id="artwork-uploader-${sectionId}"
          />
          <div class="upload-status" id="upload-status-${sectionId}"></div>
      `;
      parentElement.appendChild(uploadArea);
      
      // Initialize Uploadcare Widget and Store Instance
      const uploaderInput = uploadArea.querySelector(`#artwork-uploader-${sectionId}`);
      container._customBuilderData.uploadcareWidget = uploadcare.Widget(uploaderInput);
      container._customBuilderData.uploadcareWidget.onUploadComplete(fileInfo => {
          console.log("Upload complete:", fileInfo);
          handleArtworkUpload(fileInfo, container);
      });
  }
  
  function renderDimensionsDisplay(parentElement, container) {
      const sectionId = container.dataset.sectionId;
      const dimensionsDisplay = document.createElement('div');
      dimensionsDisplay.className = 'artwork-dimensions dynamic-control';
      dimensionsDisplay.id = `artwork-dimensions-${sectionId}`;
      dimensionsDisplay.innerHTML = 'Artwork Size: -';
      parentElement.appendChild(dimensionsDisplay);
  }
  
  function renderVariantMatrixContainer(parentElement, container) {
      const sectionId = container.dataset.sectionId;
      const matrixContainer = document.createElement('div');
      matrixContainer.className = 'variant-matrix-container dynamic-control';
      matrixContainer.id = `variant-matrix-${sectionId}`;
      matrixContainer.innerHTML = '<h4>Sizes & Quantity</h4>'; // Placeholder title
      parentElement.appendChild(matrixContainer);
  }
  
  function renderAddToCartButton(parentElement, container) {
      const sectionId = container.dataset.sectionId;
      const addToCartButton = document.createElement('button');
      addToCartButton.type = 'button';
      addToCartButton.textContent = 'Add to Cart';
      addToCartButton.className = 'btn add-to-cart-button dynamic-control';
      addToCartButton.id = `add-to-cart-${sectionId}`;
      addToCartButton.addEventListener('click', () => handleAddToCart(container));
      parentElement.appendChild(addToCartButton);
  }

  function extractProductDetails(mergedData) {
    console.log("[extractProductDetails] Starting. Received mergedData:", mergedData);
    const colors = new Set();
    const printViews = new Set();
    const imagesByColorAndView = {};
    const productsToProcess = [mergedData.mainProduct, ...mergedData.groupedProducts].filter(p => p != null);
    console.log(`[extractProductDetails] Processing ${productsToProcess.length} products.`);

    // Simple logic: Process each product's images/media directly

    productsToProcess.forEach((product, productIndex) => {
      console.log(`[extractProductDetails] Processing product ${productIndex}: ${product?.handle}`);
      let imageArray = [];
      if (product && product.media && Array.isArray(product.media) && product.media.length > 0 && typeof product.media[0] === 'object') {
          imageArray = product.media;
          console.log(`[extractProductDetails] Using product.media for ${product.handle}`);
      } else if (product && product.images && Array.isArray(product.images)) {
          imageArray = product.images;
          console.log(`[extractProductDetails] Using product.images for ${product.handle}`);
      } else {
           console.warn(`[extractProductDetails] Product ${product?.handle} has no valid media or images array.`);
           return; // Skip this product
      }

      console.log(`[extractProductDetails] Product ${product.handle} has ${imageArray.length} images listed.`);

      imageArray.forEach((image, imageIndex) => {
          if (typeof image !== 'object' || image === null || typeof image.alt === 'undefined') {
            console.warn(`[extractProductDetails] Skipping image ${imageIndex} for product ${product.handle}: Item is not an object or missing 'alt'. Item:`, image);
            return;
          }

          const imageAlt = image.alt;
          console.log(`[extractProductDetails] Processing image ${imageIndex} for product ${product.handle}. Found alt text: "${imageAlt}"`);

          if (imageAlt && imageAlt.toLowerCase() === 'hero') {
              console.log(`[extractProductDetails]   Image ${imageIndex} is HERO, skipping.`);
              return; // Skip hero image
          }

          const parsedAlt = parseImageAltText(imageAlt);
          console.log(`[extractProductDetails]   Parsed alt text for image ${imageIndex}:`, parsedAlt);

          if (parsedAlt) {
            const currentProductColor = product.options_with_values?.find(opt => opt.name.toLowerCase() === 'color')?.values[0] || parsedAlt.color;
            console.log(`[extractProductDetails]     Using color: ${currentProductColor} (from product options or parsed alt)`);

            colors.add(currentProductColor);
            printViews.add(parsedAlt.printView);

            if (!imagesByColorAndView[currentProductColor]) {
              console.log(`[extractProductDetails]       Initializing object for color: ${currentProductColor}`);
              imagesByColorAndView[currentProductColor] = {};
            }

            // Store only if not already present for this color/view
            if (!imagesByColorAndView[currentProductColor][parsedAlt.printView]) {
                 console.log(`[extractProductDetails]       Storing image data for ${currentProductColor} - ${parsedAlt.printView}`);
                 // Check for focal point data on the image object
                 let focalPoint = null;
                 if (image.presentation && image.presentation.focal_point) {
                     focalPoint = {
                         x: image.presentation.focal_point.x,
                         y: image.presentation.focal_point.y
                     };
                     console.log(`[extractProductDetails]   Found Focal Point for ${currentProductColor} - ${parsedAlt.printView}:`, focalPoint);
                 }
                 
                 imagesByColorAndView[currentProductColor][parsedAlt.printView] = {
                   src: image.src, 
                   width: image.width,
                   height: image.height,
                   parsedAlt: parsedAlt,
                   focalPoint: focalPoint // Store focal point data (can be null)
                 };
            } else {
                 console.log(`[extractProductDetails]       Skipping store: Data for ${currentProductColor} - ${parsedAlt.printView} already exists.`);
            }
          } else {
              console.log(`[extractProductDetails]   Image ${imageIndex} alt text invalid or null.`);
          }
        });
    });

    const result = {
      uniqueColors: Array.from(colors),
      uniquePrintViews: Array.from(printViews),
      imagesByColorAndView
    };
    console.log("[extractProductDetails] Finished. Returning:", result);
    return result;
  }

  function handleSelectionChange(event) {
    const target = event.currentTarget;
    const container = target.closest(customBuilderSelector);
    const builderData = container._customBuilderData;
    const sectionId = container.dataset.sectionId; // Get sectionId
    const isColorSwatch = target.classList.contains('color-swatch');
    const isPrintView = target.classList.contains('print-view-box'); // Changed from print-view-button

    // Update active state and state variable
    if (isColorSwatch) {
      container.querySelectorAll('.color-swatch').forEach(el => {
        el.classList.remove('active');
      });
      target.classList.add('active');
      const newSelectedColor = target.dataset.color;
      builderData.selectedColor = newSelectedColor;
      console.log(`State updated: selectedColor = \"${builderData.selectedColor}\"`);
      
      // --- Update Print View Images based on new color --- 
      const printViewBoxes = container.querySelectorAll('.print-view-box');
      printViewBoxes.forEach(box => {
          const printView = box.dataset.printView;
          const imgElement = box.querySelector('.print-view-image');
          const placeholderElement = box.querySelector('.print-view-image-placeholder');

          let newImageSrc = null;
          if (builderData.imagesByColorAndView[newSelectedColor] && builderData.imagesByColorAndView[newSelectedColor][printView]) {
              newImageSrc = builderData.imagesByColorAndView[newSelectedColor][printView].src;
          }

          if (imgElement) {
              if (newImageSrc) {
                  imgElement.src = newImageSrc;
                  imgElement.alt = `${printView} view for ${newSelectedColor}`;
                  imgElement.style.display = ''; // Ensure image is visible
                  if (placeholderElement) placeholderElement.style.display = 'none'; // Hide placeholder
              } else {
                  imgElement.src = ''; // Clear src if no image
                  imgElement.alt = `Preview not available for ${printView}`;
                  imgElement.style.display = 'none'; // Hide image element
                  if (placeholderElement) placeholderElement.style.display = ''; // Show placeholder
              }
          } else if (placeholderElement) {
              // Only placeholder exists, ensure it's visible if no image src
              placeholderElement.style.display = newImageSrc ? 'none' : '';
          }
      });
      // --- End Update Print View Images ---
      
    } else if (isPrintView) {
      container.querySelectorAll('.print-view-box').forEach(el => el.classList.remove('active')); // Changed selector
      target.classList.add('active');
      builderData.selectedPrintView = target.dataset.printView;
      console.log(`State updated: selectedPrintView = \"${builderData.selectedPrintView}\"`);
      
      // Update the title span with the selected view name
      const titleSpan = container.querySelector(`#selected-print-area-title-${sectionId}`);
      if (titleSpan) titleSpan.textContent = builderData.selectedPrintView;

      // --- Update Upload Area HTML based on selected Print View --- 
      const uploadStatusDiv = container.querySelector(`#upload-status-${sectionId}`);
      const uploadcareWidget = builderData.uploadcareWidget;
      const fileInfoForNewView = builderData.uploadedFileInfoByView[builderData.selectedPrintView]; // Get info for the *new* view

      if (uploadStatusDiv && uploadcareWidget) {
        if (fileInfoForNewView) {
          console.log(`Artwork found for view ${builderData.selectedPrintView}. Updating widget and status.`);
          uploadStatusDiv.textContent = 'Artwork added!'; // Or show filename
          // Update the widget to show the existing file
          // Note: This might re-trigger onUploadComplete if not handled carefully, but usually okay for setting initial value.
          uploadcareWidget.value(fileInfoForNewView); 
        } else {
          console.log(`No artwork found for view ${builderData.selectedPrintView}. Clearing widget and status.`);
          uploadStatusDiv.textContent = ''; // Clear status
          // Reset the widget 
          uploadcareWidget.value(null); 
        }
      }
      // --- End Update Upload Area --- 
    }

    console.log("Selection changed, triggering preview update...");
    updatePreviewAndOverlay(container);
    updateVariantMatrix(container);
  }

  // --- New function to handle background/overlay redrawing --- 
  function _redrawCanvasBackgroundAndOverlay(container) { 
    const builderData = container._customBuilderData;
    const fabricCanvas = builderData.fabricCanvas;
    const color = builderData.selectedColor;
    const printView = builderData.selectedPrintView;

    if (!fabricCanvas || !builderData.imagesByColorAndView || !color || !printView) {
        console.warn("[_redrawCanvasBackgroundAndOverlay] Missing data needed for redraw.");
        // Optionally clear canvas if needed
        try { // Safety try-catch for clearing
            if (fabricCanvas && !fabricCanvas.disposed) {
                fabricCanvas.setBackgroundImage(null, fabricCanvas.renderAll.bind(fabricCanvas));
                if (builderData.printableAreaRect) fabricCanvas.remove(builderData.printableAreaRect);
                if (builderData.currentlyDisplayedArtworkObject) fabricCanvas.remove(builderData.currentlyDisplayedArtworkObject);
                fabricCanvas.renderAll();
            }
        } catch(clearError) {
            console.error('[_redrawCanvasBackgroundAndOverlay] Error during preemptive clear:', clearError);
        }
      return;
    }

    const imageData = builderData.imagesByColorAndView[color]?.[printView];
    console.log(`[_redrawCanvasBackgroundAndOverlay] Redrawing for Color: ${color}, Print View: ${printView}`);

    if (imageData && imageData.src && imageData.parsedAlt) {
        console.log(`[_redrawCanvasBackgroundAndOverlay] Attempting to load image: ${imageData.src}`);
        
        // <<< Use v5.3.0 callback pattern >>>
        fabric.Image.fromURL(imageData.src, (img, isError) => {
            if (isError || !img) {
                console.error(`[_redrawCanvasBackgroundAndOverlay] Failed to load image ${imageData.src}. Error flag: ${isError}, Img object:`, img);
                // Clear background/overlay on error
                 try { // Safety try-catch for clearing
                    if (fabricCanvas && !fabricCanvas.disposed) {
                        fabricCanvas.setBackgroundImage(null, fabricCanvas.renderAll.bind(fabricCanvas));
                        if (builderData.printableAreaRect) {
                            fabricCanvas.remove(builderData.printableAreaRect);
                            builderData.printableAreaRect = null; 
                        }
                         if (builderData.currentlyDisplayedArtworkObject) {
                            fabricCanvas.remove(builderData.currentlyDisplayedArtworkObject);
                            builderData.currentlyDisplayedArtworkObject = null;
                        }
                        fabricCanvas.renderAll();
                    }
                 } catch (clearError) {
                     console.error('[_redrawCanvasBackgroundAndOverlay] Error during cleanup after image load failure:', clearError);
                 }
                return; // Stop processing on error
            }

           console.log('[_redrawCanvasBackgroundAndOverlay] Image loaded successfully via callback:', img);

           // Check canvas validity *inside* callback
           if (!fabricCanvas || fabricCanvas.disposed) { 
               console.warn('[_redrawCanvasBackgroundAndOverlay] Canvas disposed before image processing could complete (callback).');
               return;
           }

           // --- Define dimensions and scale (Existing logic) --- 
           const canvasWidth = fabricCanvas.width; 
           const canvasHeight = fabricCanvas.height; 
           const imgWidth = img.width || 1; 
           const imgHeight = img.height || 1;
           const scaleX = canvasWidth / imgWidth;
           const scaleY = canvasHeight / imgHeight;
           const scale = Math.min(scaleX, scaleY); 
           builderData.displayScaleFactor = scale; 
           
           // --- Calculate Background Position --- 
           const scaledWidth = imgWidth * scale;
           const scaledHeight = imgHeight * scale;
           const offsetX = (canvasWidth - scaledWidth) / 2;
           const offsetY = (canvasHeight - scaledHeight) / 2;
           
           // Set Background Image (Using v5.3.0 method)
           try {
               fabricCanvas.setBackgroundImage(img, fabricCanvas.renderAll.bind(fabricCanvas), { 
                   scaleX: scale,
                   scaleY: scale,
                   left: offsetX,
                   top: offsetY,
                   originX: 'left',
                   originY: 'top'
               });
               console.log('[_redrawCanvasBackgroundAndOverlay] After setBackgroundImage, canvas.backgroundImage:', fabricCanvas.backgroundImage);
            } catch (error) {
                console.error('[_redrawCanvasBackgroundAndOverlay] Error setting background image:', error);
                 // Attempt to clear background on error
                 try { 
                   fabricCanvas.setBackgroundImage(null, fabricCanvas.renderAll.bind(fabricCanvas)); 
                 } catch (clearError) {
                   console.error('[_redrawCanvasBackgroundAndOverlay] Error clearing background via setBackgroundImage:', clearError);
                 }
            }
           
           // Update Overlay Position and Size (Existing logic)
           const { scaleFactor: overlayScaleFactor, coordinates: originalOverlayCoords } = imageData.parsedAlt;
           // Store scale factor for later calculations
           builderData.currentScaleFactor = overlayScaleFactor; 
           const printableWidthInches = PRINT_AREA_DIMENSIONS[printView]?.width || 13;
           const printableHeightInches = PRINT_AREA_DIMENSIONS[printView]?.height || 17;
           const overlayWidthPx = printableWidthInches * overlayScaleFactor;
           const overlayHeightPx = printableHeightInches * overlayScaleFactor;
           const originalOverlayX = originalOverlayCoords.x;
           const originalOverlayY = originalOverlayCoords.y;
           const canvasOverlayLeft = offsetX + (originalOverlayX * scale);
           const canvasOverlayTop = offsetY + (originalOverlayY * scale);
           const scaledOverlayWidth = overlayWidthPx * scale;
           const scaledOverlayHeight = overlayHeightPx * scale;
           
           if (builderData.printableAreaRect) {
                builderData.printableAreaRect.set({
                    left: canvasOverlayLeft,
                    top: canvasOverlayTop,
                    width: scaledOverlayWidth,
                    height: scaledOverlayHeight,
                    scaleX: 1, 
                    scaleY: 1,
                    stroke: '#888888',      // Change stroke color to grey
                    strokeDashArray: [3, 3] // Finer dashes
                });
                console.log(`[_redrawCanvasBackgroundAndOverlay] Updated Fabric overlay: ${scaledOverlayWidth.toFixed(2)}px x ${scaledOverlayHeight.toFixed(2)}px`);
               
               // Update existing guide lines
               if (builderData.centerGuideH) {
                   builderData.centerGuideH.set({
                       x1: canvasOverlayLeft,
                       y1: canvasOverlayTop + scaledOverlayHeight / 2,
                       x2: canvasOverlayLeft + scaledOverlayWidth,
                       y2: canvasOverlayTop + scaledOverlayHeight / 2
                   });
               }
               if (builderData.centerGuideV) {
                    builderData.centerGuideV.set({
                       x1: canvasOverlayLeft + scaledOverlayWidth / 2,
                       y1: canvasOverlayTop,
                       x2: canvasOverlayLeft + scaledOverlayWidth / 2,
                       y2: canvasOverlayTop + scaledOverlayHeight
                   });
               }
               
           } else {
               builderData.printableAreaRect = new fabric.Rect({
                    left: canvasOverlayLeft,
                    top: canvasOverlayTop,
                    width: scaledOverlayWidth,
                    height: scaledOverlayHeight,
                    fill: 'transparent',
                    stroke: '#888888',      // Change stroke color to grey
                    strokeWidth: 2,
                    strokeDashArray: [3, 3], // Finer dashes
                    selectable: false,
                    evented: false,
                    objectCaching: false
               });
               fabricCanvas.add(builderData.printableAreaRect);
               console.log(`[_redrawCanvasBackgroundAndOverlay] Created Fabric overlay: ${scaledOverlayWidth.toFixed(2)}px x ${scaledOverlayHeight.toFixed(2)}px`);
               
               // Create guide lines
               builderData.centerGuideH = new fabric.Line(
                   [canvasOverlayLeft, canvasOverlayTop + scaledOverlayHeight / 2, canvasOverlayLeft + scaledOverlayWidth, canvasOverlayTop + scaledOverlayHeight / 2],
                   {
                       stroke: '#aaaaaa',
                       strokeWidth: 1,
                       strokeDashArray: [2, 2],
                       selectable: false,
                       evented: false
                   }
               );
               builderData.centerGuideV = new fabric.Line(
                   [canvasOverlayLeft + scaledOverlayWidth / 2, canvasOverlayTop, canvasOverlayLeft + scaledOverlayWidth / 2, canvasOverlayTop + scaledOverlayHeight],
                   {
                       stroke: '#aaaaaa',
                       strokeWidth: 1,
                       strokeDashArray: [2, 2],
                       selectable: false,
                       evented: false
                   }
               );
               fabricCanvas.add(builderData.centerGuideH, builderData.centerGuideV);
               console.log('[_redrawCanvasBackgroundAndOverlay] Created center guide lines.');
           }
           console.log('[_redrawCanvasBackgroundAndOverlay] After adding overlay, canvas objects:', fabricCanvas.getObjects());
           fabricCanvas.bringToFront(builderData.printableAreaRect); 

           // --- Handle Displaying Artwork (Existing logic) ---
           const artworkForNewView = builderData.uploadedArtworkByView[printView];
           const currentArtwork = builderData.currentlyDisplayedArtworkObject;

           if (currentArtwork && currentArtwork !== artworkForNewView) {
               console.log(`[_redrawCanvasBackgroundAndOverlay] Removing previously displayed artwork for view: ${currentArtwork.printView || '(unknown)'}`);
               fabricCanvas.remove(currentArtwork); 
           }

           if (artworkForNewView && artworkForNewView !== currentArtwork) {
               console.log(`[_redrawCanvasBackgroundAndOverlay] Adding artwork for view: ${printView}`);
               artworkForNewView.printView = printView; 
               fabricCanvas.add(artworkForNewView); 
               fabricCanvas.bringToFront(artworkForNewView); 
           } else if (!artworkForNewView) {
                console.log(`[_redrawCanvasBackgroundAndOverlay] No artwork to display for view: ${printView}`);
           }

           builderData.currentlyDisplayedArtworkObject = artworkForNewView || null;
           updateArtworkDimensionsDisplay(builderData.currentlyDisplayedArtworkObject, container);
           // --- End Handle Displaying Artwork ---

           fabricCanvas.requestRenderAll(); 
           console.log('[_redrawCanvasBackgroundAndOverlay] Render requested after setting background/overlay (callback).');

        }, { crossOrigin: 'anonymous' }); // End fabric.Image.fromURL callback
        
    } else {
          // --- Existing logic to clear background/overlay --- 
          console.warn(`[_redrawCanvasBackgroundAndOverlay] No image data for ${color}/${printView}. Clearing background/overlay.`);
          try { // Safety try-catch for clearing
              if (fabricCanvas && !fabricCanvas.disposed) { // Add check here too
                  fabricCanvas.setBackgroundImage(null, fabricCanvas.renderAll.bind(fabricCanvas));
                  if (builderData.printableAreaRect) {
                      fabricCanvas.remove(builderData.printableAreaRect);
                      builderData.printableAreaRect = null;
                  }
                  if (builderData.currentlyDisplayedArtworkObject) {
                     fabricCanvas.remove(builderData.currentlyDisplayedArtworkObject);
                     builderData.currentlyDisplayedArtworkObject = null;
                  }
                  fabricCanvas.renderAll();
             }
          } catch (clearError) {
               console.error('[_redrawCanvasBackgroundAndOverlay] Error during no-image-data clear:', clearError);
          }
    }
  }

  function updatePreviewAndOverlay(container) {
    const builderData = container._customBuilderData;
    const fabricCanvas = builderData.fabricCanvas;
    const color = builderData.selectedColor;
    const printView = builderData.selectedPrintView;

    console.log(`[updatePreviewAndOverlay] Triggered for Color: ${color}, Print View: ${printView}`);
    
    if (!fabricCanvas) {
        console.error("[updatePreviewAndOverlay] Fabric canvas not available. Cannot update preview.");
        return;
    }

    // Call the refactored drawing function
    _redrawCanvasBackgroundAndOverlay(container);
    
    // The artwork handling logic is now primarily within _redrawCanvasBackgroundAndOverlay
    // to ensure it's consistent during selection changes and resizes.
    // We might only need minimal UI updates here if any.
    
    // Example: Ensure correct artwork dimensions are displayed immediately after selection change
    const artworkForThisView = builderData.uploadedArtworkByView[printView];
    updateArtworkDimensionsDisplay(artworkForThisView || null, container);
  }

  function handleArtworkUpload(fileInfo, container) {
      const builderData = container._customBuilderData;
      const fabricCanvas = builderData.fabricCanvas;
      const sectionId = container.dataset.sectionId;
      const statusDiv = container.querySelector(`#upload-status-${sectionId}`);
      const currentPrintView = builderData.selectedPrintView;

      if (!fabricCanvas || !builderData.printableAreaRect) {
          console.error("Cannot add artwork: Fabric canvas or printable area not ready.");
          if(statusDiv) statusDiv.textContent = "Error: Canvas not ready.";
          return;
      }
      if(statusDiv) statusDiv.textContent = "Processing uploaded artwork...";

      if (builderData.uploadedArtwork) {
          fabricCanvas.remove(builderData.uploadedArtwork);
          builderData.uploadedArtwork = null;
      }

      const imageUrl = fileInfo.cdnUrl;
      fabric.Image.fromURL(imageUrl, (img) => {
          console.log('Artwork loaded into Fabric:', img);
          const overlay = builderData.printableAreaRect;
          if (!overlay) {
              console.error("Cannot position artwork: Printable area overlay is missing.");
              if (statusDiv) statusDiv.textContent = "Error: Overlay missing."; // Update status
              return;
          }
          
          // Calculate scale to fit artwork within the overlay bounds
          const overlayWidth = overlay.width;
          const overlayHeight = overlay.height;
          const artworkWidth = img.width;
          const artworkHeight = img.height;
          
          const scaleX = overlayWidth / artworkWidth;
          const scaleY = overlayHeight / artworkHeight;
          // Use the smaller scale factor to fit, maybe with a slight padding (e.g., 90%)
          const scale = Math.min(scaleX, scaleY) * 0.9;
          
          const overlayCenterX = overlay.left + overlay.width / 2;
          const overlayCenterY = overlay.top + overlay.height / 2;

          // --- Custom Control Definitions ---
          // Action Handler: Center Horizontally
          function centerHorizontallyAction(eventData, transform) {
              const target = transform.target;
              const canvas = target.canvas;
              const container = canvas.getElement().closest(customBuilderSelector);
              const builderData = container._customBuilderData;
              const printableArea = builderData.printableAreaRect;
              
              if (target && printableArea) {
                  const areaCenterX = printableArea.left + printableArea.width / 2;
                  target.set({
                      left: areaCenterX,
                      originX: 'center'
                  });
                  target.setCoords();
                  canvas.renderAll();
                  checkBounds(target, container); // Re-check bounds after centering
                  updateArtworkDimensionsDisplay(target, container); // Update display
                  return true; // Indicate action handled
              }
              return false;
          }

          // Action Handler: Center Vertically
          function centerVerticallyAction(eventData, transform) {
              const target = transform.target;
              const canvas = target.canvas;
              const container = canvas.getElement().closest(customBuilderSelector);
              const builderData = container._customBuilderData;
              const printableArea = builderData.printableAreaRect;

              if (target && printableArea) {
                  const areaCenterY = printableArea.top + printableArea.height / 2;
                  target.set({
                      top: areaCenterY,
                      originY: 'center'
                  });
                  target.setCoords();
                  canvas.renderAll();
                  checkBounds(target, container); // Re-check bounds after centering
                  updateArtworkDimensionsDisplay(target, container); // Update display
                  return true; // Indicate action handled
              }
              return false;
          }

          // Render function for controls (Simple Text for now)
          function renderTextIcon(ctx, left, top, styleOverride, fabricObject, text) {
            const size = this.cornerSize || 14; // Use cornerSize or default
            ctx.save();
            ctx.translate(left, top);
            // ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle)); // Don't rotate the control icon itself
            ctx.fillStyle = styleOverride.cornerColor || 'rgba(0,0,0,0.7)'; // Background
            ctx.fillRect(-size / 2, -size / 2, size, size); // Draw background square
            ctx.fillStyle = 'white'; // Text color
            ctx.font = `${size * 0.7}px sans-serif`; // Adjust font size relative to control size
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, 0, 0); // Draw text centered
            ctx.restore();
          }
          
          // --- End Custom Control Definitions ---
          
          // Center the scaled artwork within the overlay
          img.set({
              scaleX: scale,
              scaleY: scale,
              left: overlayCenterX,
              top: overlayCenterY,
              originX: 'center', // Center the image origin
              originY: 'center',
              borderColor: 'blue',
              cornerColor: 'blue', // Keep default controls blue for now
              cornerSize: 10,
              touchCornerSize: 24, // Ensure touch targets are decent size
              transparentCorners: false,
              // Assign custom controls (Adding to default set)
              // controls: fabric.Object.prototype.controls // Start with default controls
          });

          // Add custom controls to the default set
          img.controls.centerH = new fabric.Control({
            x: -0.5, // Position left of bottom-center
            y: 0.5, // Position at bottom-center vertically
            offsetY: 15, // Push below the corner slightly
            cursorStyle: 'pointer',
            actionHandler: centerHorizontallyAction,
            render: function(ctx, left, top, styleOverride, fabricObject) {
                renderTextIcon.call(this, ctx, left, top, styleOverride, fabricObject, '↔'); // Use arrow symbol
            },
            cornerSize: 20 // Make controls slightly larger
          });

          img.controls.centerV = new fabric.Control({
            x: 0.5, // Position right of bottom-center
            y: 0.5, // Position at bottom-center vertically
            offsetY: 15, // Push below the corner slightly
            cursorStyle: 'pointer',
            actionHandler: centerVerticallyAction,
            render: function(ctx, left, top, styleOverride, fabricObject) {
                renderTextIcon.call(this, ctx, left, top, styleOverride, fabricObject, '↕'); // Use arrow symbol
            },
             cornerSize: 20 // Make controls slightly larger
          });

          // Optional: If you want ONLY the centering controls and maybe rotation:
          // img.controls = {
          //     mtr: fabric.Object.prototype.controls.mtr, // Keep rotation
          //     centerH: img.controls.centerH,
          //     centerV: img.controls.centerV
          // };

          // Make sure the controls are updated on the object
          img.setControlVisible('centerH', true);
          img.setControlVisible('centerV', true);


          builderData.uploadedArtwork = img;
          fabricCanvas.add(img);
          fabricCanvas.setActiveObject(img); 
          fabricCanvas.renderAll();
          if(statusDiv) statusDiv.textContent = "Artwork added!";
          checkBounds(img, container);
          updateArtworkDimensionsDisplay(img, container);

          // Remove previously displayed artwork from canvas (if any)
          if (builderData.currentlyDisplayedArtworkObject && builderData.currentlyDisplayedArtworkObject !== img) {
              console.log(`[handleArtworkUpload] Removing previous artwork for view: ${builderData.currentlyDisplayedArtworkObject.printView}`);
              fabricCanvas.remove(builderData.currentlyDisplayedArtworkObject);
          }

          // Store the new artwork object associated with the current view
          builderData.uploadedArtworkByView[currentPrintView] = img;
          // Store the corresponding fileInfo object
          builderData.uploadedFileInfoByView[currentPrintView] = fileInfo;
          // Update the tracker for currently displayed artwork
          builderData.currentlyDisplayedArtworkObject = img;
          img.printView = currentPrintView; // Add printView property to the Fabric object itself

          console.log(`[handleArtworkUpload] Artwork added for view ${currentPrintView}. Object:`, img);

      }, { crossOrigin: 'anonymous' });
  }

  function checkBounds(obj, container) {
      const builderData = container._customBuilderData;
      const canvas = builderData.fabricCanvas;
      const printableAreaRect = builderData.printableAreaRect;
      const sectionId = container.dataset.sectionId;
      const notificationElement = container.querySelector(`#bounds-notification-${sectionId}`);

      if (!obj || !canvas || !printableAreaRect) return;
      obj.setCoords();
      const objBoundingBox = obj.getBoundingRect();
      const areaBounds = {
          left: printableAreaRect.left,
          top: printableAreaRect.top,
          right: printableAreaRect.left + printableAreaRect.width,
          bottom: printableAreaRect.top + printableAreaRect.height
      };
      const isWithinBounds = (
          objBoundingBox.left >= areaBounds.left &&
          objBoundingBox.top >= areaBounds.top &&
          (objBoundingBox.left + objBoundingBox.width) <= areaBounds.right &&
          (objBoundingBox.top + objBoundingBox.height) <= areaBounds.bottom
      );

      // Show/Hide Notification
      if (notificationElement) {
          if (isWithinBounds) {
              notificationElement.classList.add('hidden');
              obj.set('borderColor', 'blue'); // Keep blue border when inside
          } else {
              notificationElement.classList.remove('hidden');
              obj.set('borderColor', 'red'); // Keep red border when outside
              // console.warn("Artwork is outside the printable area!"); // Keep or remove console warning
          }
      } else {
          // Fallback border color logic if notification element not found
          obj.set('borderColor', isWithinBounds ? 'blue' : 'red');
      }

      canvas.requestRenderAll();
      updateArtworkDimensionsDisplay(obj, container);
  }

  function updateArtworkDimensionsDisplay(obj, container) {
      const builderData = container._customBuilderData;
      // Removed printableAreaRect dependency as it's not directly needed for scale
      const sectionId = container.dataset.sectionId;
      const displayElement = container.querySelector(`#artwork-dimensions-${sectionId}`);
      
      if (!obj || !displayElement) {
          if (displayElement) displayElement.innerHTML = 'Artwork Size: -';
          return;
      }

      // Get the scale factor (pixels per inch) for the CURRENT background image view
      const pixelsPerInch = builderData.currentScaleFactor;
      const displayScale = builderData.displayScaleFactor;

      if (!pixelsPerInch || typeof pixelsPerInch !== 'number' || pixelsPerInch <= 0) {
          console.warn(`[updateArtworkDimensionsDisplay] Cannot calculate dimensions: Invalid pixelsPerInch (${pixelsPerInch}). Check background image alt text.`);
          displayElement.innerHTML = 'Artwork Size: (Scale N/A)';
          return;
      }
      if (!displayScale || typeof displayScale !== 'number' || displayScale <= 0) {
          console.warn(`[updateArtworkDimensionsDisplay] Cannot calculate dimensions: Invalid displayScale (${displayScale}).`);
          displayElement.innerHTML = 'Artwork Size: (Scale N/A)';
          return;
      }

      // Get the artwork's current size ON THE CANVAS in pixels
      const widthPx = obj.getScaledWidth();
      const heightPx = obj.getScaledHeight();

      // Calculate the effective scale on screen (pixels per inch accounting for display scaling)
      const effectivePixelsPerInch = pixelsPerInch * displayScale;

      // Convert canvas pixel size to "real-world" inches relative to the printable area definition
      const widthIn = (widthPx / effectivePixelsPerInch).toFixed(2);
      const heightIn = (heightPx / effectivePixelsPerInch).toFixed(2);
      
      displayElement.innerHTML = `Artwork Size: ${widthIn}" x ${heightIn}"`;
  }

  function updateVariantMatrix(container) {
      const builderData = container._customBuilderData;
      const sectionId = container.dataset.sectionId;
      const matrixContainer = container.querySelector(`#variant-matrix-${sectionId}`);
      const selectedColor = builderData.selectedColor;
      
      if (!builderData || !builderData.mergedData || !matrixContainer || !selectedColor) {
          console.warn("Cannot update variant matrix: Missing data, container, or selected color.", { builderData, matrixContainer, selectedColor });
          if (matrixContainer) matrixContainer.innerHTML = '<h4>Sizes & Quantity</h4><p>Select a color.</p>';
          return;
      }
      
      console.log(`Updating variant matrix for color: ${selectedColor}`);
      
      // Find the product object that actually corresponds to the selectedColor
      console.log(`[updateVariantMatrix] Searching for product with color: "${selectedColor}"`); // Log target color
      const productsToCheck = [builderData.mergedData.mainProduct, ...builderData.mergedData.groupedProducts];
      console.log(`[updateVariantMatrix] Checking ${productsToCheck.length} total products.`); // Log number of products
      
      const relevantProduct = productsToCheck.find((p, index) => {
          console.log(`[updateVariantMatrix] --- Checking Product Index ${index} ---`);
          if (!p) {
              console.log(`[updateVariantMatrix] Product at index ${index} is null/undefined.`);
              return false;
          }
          console.log(`[updateVariantMatrix] Product Handle: ${p.handle}`);

          // Check for options_with_values OR options
          let optionsArray = p.options_with_values || p.options;

          if (!optionsArray || !Array.isArray(optionsArray)) {
              console.log(`[updateVariantMatrix] Product ${p.handle} missing 'options_with_values' AND 'options'. Full product object:`, JSON.stringify(p)); // Log full product if options missing
              return false;
          }
          
          console.log(`[updateVariantMatrix] Product ${p.handle} using options array:`, JSON.stringify(optionsArray)); 

          // Find the color option (assuming name property exists)
          const colorOption = optionsArray.find(opt => typeof opt.name === 'string' && opt.name.toLowerCase() === 'color');
          if (!colorOption) {
              console.log(`[updateVariantMatrix] Product ${p.handle} has no 'color' option.`);
              return false;
          }
          console.log(`[updateVariantMatrix] Product ${p.handle} found colorOption:`, JSON.stringify(colorOption));

          // Check for current_value (from options_with_values) OR values[0] (from options)
          let productActualColor = colorOption.current_value || (colorOption.values && colorOption.values.length > 0 ? colorOption.values[0] : null);

          if (productActualColor === null || typeof productActualColor !== 'string') {
              console.log(`[updateVariantMatrix] Product ${p.handle} could not extract color value from option:`, JSON.stringify(colorOption));
              return false;
          }

          console.log(`[updateVariantMatrix] Product ${p.handle} extracted color value: "${productActualColor}" (Type: ${typeof productActualColor})`);
          
          const isMatch = productActualColor.trim().toLowerCase() === selectedColor.trim().toLowerCase();
          console.log(`[updateVariantMatrix] Comparing: "${productActualColor}".trim().toLowerCase() === "${selectedColor}".trim().toLowerCase() => ${isMatch}`);
          
          return isMatch;
      });

      if (!relevantProduct) {
          console.warn(`No product object found in mergedData for selected color: ${selectedColor}`);
          matrixContainer.innerHTML = '<h4>Sizes & Quantity</h4><p>Variant information not available for this color.</p>';
          return;
      }

      // --- Generate Improved Variant Matrix UI --- 
      matrixContainer.innerHTML = ''; // Clear previous
      
      const title = document.createElement('h4');
      title.textContent = 'Sizes & Quantity';
      matrixContainer.appendChild(title);
      
      const sizeContainer = document.createElement('div');
      sizeContainer.className = 'variant-matrix-sizes'; // Add class for styling
      matrixContainer.appendChild(sizeContainer);

      const sizeOptionName = relevantProduct.options?.find(opt => opt.name.toLowerCase() === 'size')?.name || 'Size';
      
      // Group variants by size
      const variantsBySize = {};
      relevantProduct.variants.forEach(variant => {
          const sizeValue = variant[variant.options.find(optName => optName.toLowerCase() === sizeOptionName.toLowerCase())] || variant.option1 || variant.option2 || variant.option3; // Find the size value
          if (!variantsBySize[sizeValue]) {
              variantsBySize[sizeValue] = [];
          }
          variantsBySize[sizeValue].push(variant);
      });

      // Generate Size boxes and quantity inputs
      Object.keys(variantsBySize).forEach(size => {
           const sizeBox = document.createElement('div');
           sizeBox.className = 'variant-size-box'; // Class for styling
           
           const sizeLabel = document.createElement('div');
           sizeLabel.className = 'variant-size-label';
           sizeLabel.textContent = size;
           sizeBox.appendChild(sizeLabel);
           
           // Assuming one variant per size for this product structure, find the variant
           const variant = variantsBySize[size][0]; 
           if (variant) {
               const priceLabel = document.createElement('div');
               priceLabel.className = 'variant-price-label';
               priceLabel.textContent = formatMoney(variant.price);
               sizeBox.appendChild(priceLabel);

               const quantityInput = document.createElement('input');
               quantityInput.type = 'number';
               quantityInput.min = '0';
               quantityInput.value = '0';
               quantityInput.className = 'quantity-input';
               quantityInput.dataset.variantId = variant.id;
               quantityInput.addEventListener('change', () => updateTotalPrice(container));
               sizeBox.appendChild(quantityInput);
           } else {
               // Handle cases where variant might be missing for a size (optional)
               sizeBox.innerHTML += '<p>Unavailable</p>';
           }
           
           sizeContainer.appendChild(sizeBox);
      });

      // Add Total Price Display
      const totalPriceDiv = document.createElement('div');
      totalPriceDiv.className = 'total-price';
      totalPriceDiv.id = `total-price-${sectionId}`; // Ensure ID matches updateTotalPrice
      totalPriceDiv.textContent = 'Total: ' + formatMoney(0); // Initial total
      matrixContainer.appendChild(totalPriceDiv);

      // --- End Generate Improved Variant Matrix UI --- 
  }

  function updateTotalPrice(container) {
      const builderData = container._customBuilderData;
      const sectionId = container.dataset.sectionId;
      const totalPriceElement = container.querySelector(`#total-price-${sectionId}`);
      const matrixContainer = container.querySelector(`#variant-matrix-${sectionId}`);
      if (!builderData || !builderData.mergedData || !totalPriceElement || !matrixContainer) return;
      let currentTotal = 0;
      const quantityInputs = matrixContainer.querySelectorAll('.quantity-input');
      quantityInputs.forEach(input => {
          const quantity = parseInt(input.value, 10);
          const variantId = parseInt(input.dataset.variantId, 10);
          if (quantity > 0 && !isNaN(variantId)) {
              let variantPrice = 0;
              const products = [builderData.mergedData.mainProduct, ...builderData.mergedData.groupedProducts];
              for (const product of products) {
                  const variant = product?.variants.find(v => v.id === variantId);
                  if (variant) {
                      variantPrice = variant.price;
                      break;
                  }
              }
              currentTotal += quantity * variantPrice;
          }
      });
      totalPriceElement.textContent = 'Total: ' + formatMoney(currentTotal);
  }

  function formatMoney(priceInCents) {
      if (typeof priceInCents !== 'number' || isNaN(priceInCents)) return '';
      return '$' + (priceInCents / 100).toFixed(2);
  }

  function handleAddToCart(container) {
      const builderData = container._customBuilderData;
      const sectionId = container.dataset.sectionId;
      const matrixContainer = container.querySelector(`#variant-matrix-${sectionId}`);
      if (!builderData || !builderData.fabricCanvas || !matrixContainer) {
          console.error("Cannot add to cart: Missing builder data, canvas, or matrix.");
          alert("Error preparing item for cart.");
          return;
      }
      const properties = {};
      const selectedColorEl = container.querySelector('.color-swatch.active');
      const selectedPrintViewEl = container.querySelector('.print-view-button.active');
      properties['Color'] = selectedColorEl ? selectedColorEl.dataset.color : 'N/A';
      properties['Print View'] = selectedPrintViewEl ? selectedPrintViewEl.dataset.printView : 'N/A';
      if (builderData.uploadedArtwork && builderData.uploadedArtwork.getSrc()) {
          properties['_Artwork URL'] = builderData.uploadedArtwork.getSrc(); 
      }
      const items = [];
      const quantityInputs = matrixContainer.querySelectorAll('.quantity-input');
      quantityInputs.forEach(input => {
          const quantity = parseInt(input.value, 10);
          const variantId = parseInt(input.dataset.variantId, 10);
          if (quantity > 0 && !isNaN(variantId)) {
              items.push({
                  id: variantId,
                  quantity: quantity,
                  // Properties will be added after snapshot
              });
          }
      });
      if (items.length === 0) {
          alert("Please select quantity for at least one size.");
          return;
      }
      let snapshotDataUrl = 'N/A';
      try {
          if (builderData.printableAreaRect) builderData.printableAreaRect.set('selectable', false);
          if (builderData.uploadedArtwork) builderData.uploadedArtwork.set('selectable', false);
          snapshotDataUrl = builderData.fabricCanvas.toDataURL({
              format: 'png',
              quality: 0.8,
          });
          properties['_Preview_Snapshot'] = snapshotDataUrl; 
      } catch (e) {
          console.error("Error generating canvas snapshot:", e);
          properties['_Preview_Snapshot'] = 'Error generating snapshot';
      }
      const finalItems = items.map(item => ({ ...item, properties: { ...properties } }));
      console.log("Adding to cart with properties:", finalItems);
      const addButton = container.querySelector(`#add-to-cart-${sectionId}`);
      if(addButton) addButton.disabled = true;
      fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: finalItems })
      })
      .then(response => response.json())
      .then(cart => {
          console.log("Cart updated:", cart);
          alert("Items added to cart!");
          if(addButton) addButton.disabled = false;
      })
      .catch((error) => {
          console.error('Error adding items to cart:', error);
          alert("Error adding items to cart. Please try again.");
          if(addButton) addButton.disabled = false;
      });
  }

  function parseImageAltText(altText) {
    if (!altText || typeof altText !== 'string' || altText.toLowerCase() === 'hero') {
      return null;
    }
    const parts = altText.split(ALT_TEXT_SEPARATOR).map(part => part.trim());
    if (parts.length !== 4) {
      console.warn(`Invalid alt text format: "${altText}"`);
      return null;
    }
    const [color, printView, scaleFactorStr, coordinatesStr] = parts;
    const scaleFactor = parseFloat(scaleFactorStr);
    if (isNaN(scaleFactor) || scaleFactor <= 0) {
      console.warn(`Invalid scale factor: "${scaleFactorStr}"`);
      return null;
    }
    const coordinateParts = coordinatesStr.split(COORDINATE_SEPARATOR).map(part => part.trim());
    if (coordinateParts.length !== 2) {
      console.warn(`Invalid coordinates format: "${coordinatesStr}"`);
      return null;
    }
    const x = parseInt(coordinateParts[0], 10);
    const y = parseInt(coordinateParts[1], 10);
    if (isNaN(x) || isNaN(y) || x < 0 || y < 0) {
      console.warn(`Invalid coordinate values: X=${x}, Y=${y}`);
      return null;
    }
    return { color, printView, scaleFactor, coordinates: { x, y } };
  }

  // --- New function containing canvas-dependent UI rendering --- 
  function _finishRenderUI(container) {
      const sectionId = container.dataset.sectionId;
      const builderData = container._customBuilderData;
      const controlsColumn = container.querySelector('.builder-controls-column');
      const mergedData = builderData.mergedData;

      // Ensure canvas and data are ready before proceeding
      if (!builderData.fabricCanvas) {
          console.error(`[_finishRenderUI - ${sectionId}] Called but Fabric canvas is not ready. Aborting.`);
          return;
      }
       if (!mergedData || !mergedData.mainProduct) {
          console.error(`[_finishRenderUI - ${sectionId}] Called but mergedData is not ready. Aborting.`);
           // Optionally display an error message
           if (controlsColumn) controlsColumn.innerHTML = '<p>Error: Product data missing.</p>';
          return;
      }
      if (!controlsColumn) {
           console.error(`[_finishRenderUI - ${sectionId}] Controls column not found. Aborting.`);
           return;
      }
      
      console.log(`[_finishRenderUI - ${sectionId}] Canvas ready. Rendering remaining UI elements.`);

      // --- Remove uiFinished flag --- 
      // if (builderData.uiFinished) { ... }
      // builderData.uiFinished = true; 

      // --- Explicitly get canvas reference --- 
      const fabricCanvas = builderData.fabricCanvas;
      if (!fabricCanvas) {
          console.error(`[_finishRenderUI - ${sectionId}] CRITICAL: Canvas reference missing even after ready flag. Aborting.`);
          return;
      }
      // --- End Get Canvas Reference --- 

      // Extract Data 
      const { uniqueColors, uniquePrintViews, imagesByColorAndView } = extractProductDetails(mergedData);
      console.log(`[_finishRenderUI - ${sectionId}] Extracted Details:`, { uniqueColors, uniquePrintViews }); 
      
      // Store processed data back into container state (ensure these exist)
      builderData.imagesByColorAndView = imagesByColorAndView;
      builderData.uniqueColors = uniqueColors; // Store these too if needed elsewhere
      builderData.uniquePrintViews = uniquePrintViews;

      // Clear previous dynamic controls (ensure controlsColumn exists)
      controlsColumn.querySelectorAll('.dynamic-control').forEach(el => el.remove());

      // Render Controls
      renderColorSwatches(uniqueColors, controlsColumn, container);
      renderPrintViews(uniquePrintViews, controlsColumn, container);
      renderUploadArea(controlsColumn, container);
      renderDimensionsDisplay(controlsColumn, container);
      renderVariantMatrixContainer(controlsColumn, container);
      renderAddToCartButton(controlsColumn, container);

      // Initial UI Updates based on container state
      builderData.selectedColor = uniqueColors.length > 0 ? uniqueColors[0] : null;
      builderData.selectedPrintView = uniquePrintViews.length > 0 ? uniquePrintViews[0] : null;

      // Set initial scale factor based on initial view
      const initialColor = builderData.selectedColor;
      const initialView = builderData.selectedPrintView;
      if (initialColor && initialView && imagesByColorAndView[initialColor] && imagesByColorAndView[initialColor][initialView]) {
          builderData.currentScaleFactor = imagesByColorAndView[initialColor][initialView].parsedAlt.scaleFactor;
          console.log(`[_finishRenderUI - ${sectionId}] Initial Scale Factor set to: ${builderData.currentScaleFactor} for ${initialColor} - ${initialView}`);
      } else {
          builderData.currentScaleFactor = null; 
          console.warn(`[_finishRenderUI - ${sectionId}] Could not set initial scale factor. Missing data for Color: ${initialColor}, View: ${initialView}`);
      }

      console.log(`[_finishRenderUI - ${sectionId}] Initial State Set:`, { 
          selectedColor: builderData.selectedColor, 
          selectedPrintView: builderData.selectedPrintView 
      });

      // Update matrix and preview now that controls are rendered and canvas is ready
      updateVariantMatrix(container); // Pass canvas ref no longer needed as it's in builderData
      updatePreviewAndOverlay(container); // Pass canvas ref no longer needed as it's in builderData

      // --- Mobile Edit Overlay Listener REMOVED --- 
      /*
      const editOverlay = container.querySelector(`#canvas-edit-overlay-${sectionId}`);
      const editButton = container.querySelector(`#canvas-edit-button-${sectionId}`);
      const canvasEl = container.querySelector(`#builder-canvas-${sectionId}`); // Get the actual canvas element

      if (editButton && editOverlay && canvasEl) {
          editButton.addEventListener('click', () => {
              console.log('Edit button clicked, enabling canvas interaction.');
              editOverlay.classList.add('is-hidden'); // Hide the overlay
              canvasEl.classList.add('is-active');    // Enable pointer events on canvas via CSS
              // Optionally, re-enable touch scrolling on the Fabric canvas object itself if needed
              // if (builderData.fabricCanvas) {
              //    builderData.fabricCanvas.allowTouchScrolling = false; // Or true, depending on desired behavior after edit starts
              // }
          });
      } else {
          console.warn(`[_finishRenderUI - ${sectionId}] Could not find mobile edit overlay elements.`);
      }
      */
      // --- End Mobile Edit Overlay Listener --- 
  }
  // --- End _finishRenderUI --- 

  // --- Function to Setup Centering Controls ---
  function _setupCenteringControls(container, fabricCanvas) {
    const sectionId = container.dataset.sectionId;
    const controlsDiv = container.querySelector(`#centering-controls-${sectionId}`);
    const centerHButton = container.querySelector(`#center-h-${sectionId}`);
    const centerVButton = container.querySelector(`#center-v-${sectionId}`);
    const builderData = container._customBuilderData;

    if (!controlsDiv || !centerHButton || !centerVButton) {
        console.warn('[_setupCenteringControls] Centering control elements not found.');
        return;
    }

    // Show/Hide controls based on selection
    fabricCanvas.on('selection:created', (e) => {
        if (e.selected && e.selected.length === 1 && e.selected[0].type === 'image') {
            controlsDiv.classList.add('visible');
        }
    });
    fabricCanvas.on('selection:cleared', () => {
        controlsDiv.classList.remove('visible');
    });
    fabricCanvas.on('selection:updated', (e) => {
        // Handles cases where selection changes without clearing first
         if (e.selected && e.selected.length === 1 && e.selected[0].type === 'image') {
            controlsDiv.classList.add('visible');
        } else {
            controlsDiv.classList.remove('visible');
        }
    });

    // Center Horizontally
    centerHButton.addEventListener('click', () => {
        const activeObject = fabricCanvas.getActiveObject();
        const printableArea = builderData.printableAreaRect;
        if (activeObject && printableArea) {
            const areaCenterX = printableArea.left + printableArea.width / 2;
            activeObject.set({
                left: areaCenterX,
                originX: 'center' 
            });
            activeObject.setCoords(); // Update coordinates after setting
            fabricCanvas.renderAll();
            checkBounds(activeObject, container); // Re-check bounds
            updateArtworkDimensionsDisplay(activeObject, container); // Update display
        }
    });

    // Center Vertically
    centerVButton.addEventListener('click', () => {
        const activeObject = fabricCanvas.getActiveObject();
        const printableArea = builderData.printableAreaRect;
        if (activeObject && printableArea) {
            const areaCenterY = printableArea.top + printableArea.height / 2;
            activeObject.set({
                top: areaCenterY,
                originY: 'center'
            });
            activeObject.setCoords(); // Update coordinates after setting
            fabricCanvas.renderAll();
            checkBounds(activeObject, container); // Re-check bounds
            updateArtworkDimensionsDisplay(activeObject, container); // Update display
        }
    });

     console.log('[_setupCenteringControls] Event listeners for centering controls added.');
  }
}
/* ===== Custom Product Builder Logic End ===== */
